import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { FiMail } from 'react-icons/fi';

export default function ResetPasswordPage() {
  const { resetPasswordForEmail } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      await resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
        captchaToken: 'xxxxx' // Needed for production but optional locally
      });
      setSuccess(true);
      setEmail(''); // Clear the form
    } catch (err: any) {
      console.error('Reset password error:', err);
      if (err.message.includes('email')) {
        setError(t('terminal.auth.errors.invalidEmail'));
      } else {
        setError(t('terminal.auth.errors.resetPasswordFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 px-4">
      <div className="max-w-md w-full space-y-8 p-8 bg-base-100 rounded-lg shadow-xl border border-primary/10">
        <div className="text-center">
          <h2 className="glitch text-2xl font-bold" data-text={t('terminal.auth.resetPassword.title')}>
            {t('terminal.auth.resetPassword.title')}
          </h2>
          
          <p className="mt-2 text-sm text-base-content/70">
            {t('terminal.auth.resetPassword.description')}
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="alert alert-success">
              <FiMail className="w-6 h-6" />
              <span>{t('terminal.auth.resetPassword.success')}</span>
            </div>
            <Link 
              to="/login"
              className="btn btn-outline btn-primary w-full"
            >
              {t('terminal.auth.backToLogin')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {error && (
              <div className="alert alert-error">
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                <span className="label-text">{t('terminal.auth.email')}</span>
              </label>
              <input
                id="email"
                type="email"
                className="input input-bordered w-full"
                placeholder={t('terminal.auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <button 
              type="submit" 
              className={`btn btn-primary w-full ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? t('common.sending') : t('terminal.auth.resetPassword.submit')}
            </button>

            <div className="text-center">
              <Link 
                to="/login"
                className="link link-primary text-sm"
              >
                {t('terminal.auth.backToLogin')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
